import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Card, CircularProgress, Stack, Typography } from "@mui/material";
import SecureBoot from "components/modules/SingleDashboard/SecureBoot";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { devicesActions } from "../redux/slice/devices.slice";
import { uiActions } from "../redux/slice/ui.slice";
import { GET_DEVICES } from "../redux/types/devices.types";

const SecureBootDeviceUsersPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const devices = useSelector(devicesActions.selectors.getDevices);
  const selectedDevice = useSelector(devicesActions.selectors.getSelectedDevice);

  const [isFetchingDevices, setIsFetchingDevices] = useState(true);
  const [deviceMissing, setDeviceMissing] = useState(false);
  const hasSyncedDeviceRef = useRef(false);

  useEffect(() => {
    dispatch(uiActions.actions.setSelectedNav("Secure Boot"));
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingDevices(true);
    dispatch({ type: GET_DEVICES });
  }, [dispatch]);

  const deviceList = useMemo(() => {
    return Array.isArray(devices) ? devices : [];
  }, [devices]);

  const selectedDeviceId = useMemo(() => {
    return (
      selectedDevice?._id ||
      selectedDevice?.deviceId ||
      selectedDevice?.id ||
      null
    );
  }, [selectedDevice]);

  useEffect(() => {
    if (!id) {
      setIsFetchingDevices(false);
      setDeviceMissing(true);
      return;
    }

    if (!deviceList.length) {
      return;
    }

    const matchingDevice = deviceList.find((device) => {
      const identifier = device?._id || device?.deviceId || device?.id;
      return identifier === id;
    });

    if (matchingDevice) {
      setIsFetchingDevices(false);
      setDeviceMissing(false);

      if (selectedDeviceId !== id || !hasSyncedDeviceRef.current) {
        dispatch(devicesActions.actions.setSelectedDevice(matchingDevice));
        dispatch(devicesActions.actions.setCurrentDevice(matchingDevice));
        hasSyncedDeviceRef.current = true;
      }
    } else {
      setIsFetchingDevices(false);
      setDeviceMissing(true);
    }
  }, [deviceList, dispatch, id, selectedDeviceId]);

  const handleBack = () => {
    navigate("/secureboot");
  };

  if (isFetchingDevices) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (deviceMissing) {
    return (
      <Stack spacing={3} p={4} alignItems="flex-start">
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to SecureBoot Devices
        </Button>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Device not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn&apos;t find a SecureBoot-enabled device matching the provided ID.
            Please return to the SecureBoot devices list and choose another entry.
          </Typography>
        </Card>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ px: 3, py: 1 }}
          >
            Back to SecureBoot Devices
          </Button>
        </Box>
        <Box sx={{ width: "100%" }}>
          <SecureBoot />
        </Box>
      </Stack>
    </Box>
  );
};

export default SecureBootDeviceUsersPage;
